/**
 * IndexedDB storage service
 * 
 * KPE-865
 * 
 * @author Manjit Latthe
 * @copyright 2025
 * @version 1.0.0
 */
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private dbName = 'UserDataDB';
  private version = 1;
  private db: IDBDatabase | null = null;
  private secretKey = 'MyContentPortal2025Key';

  private encrypt(data: string): string {
    let encrypted = '';
    for (let i = 0; i < data.length; i++) {
      const charCode = data.charCodeAt(i) ^ this.secretKey.charCodeAt(i % this.secretKey.length);
      encrypted += charCode.toString(16).padStart(2, '0');
    }
    return encrypted;
  }

  private decrypt(encryptedData: string): string {
    let decrypted = '';
    for (let i = 0; i < encryptedData.length; i += 2) {
      const charCode = parseInt(encryptedData.substring(i, i + 2), 16) ^ this.secretKey.charCodeAt((i / 2) % this.secretKey.length);
      decrypted += String.fromCharCode(charCode);
    }
    return decrypted;
  }

  async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };
      
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('userData')) {
          db.createObjectStore('userData', { keyPath: 'key' });
        }
      };
    });
  }

  async setItem(key: string, value: any): Promise<void> {
    if (!this.db) await this.initDB();
    
    const encryptedValue = this.encrypt(JSON.stringify(value));
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['userData'], 'readwrite');
      const store = transaction.objectStore('userData');
      const request = store.put({ key, value: encryptedValue });
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async getItem(key: string): Promise<any> {
    if (!this.db) await this.initDB();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['userData'], 'readonly');
      const store = transaction.objectStore('userData');
      const request = store.get(key);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const storedValue = request.result?.value;
        if (storedValue) {
          try {
            // Try to decrypt first (for encrypted data)
            const decryptedValue = this.decrypt(storedValue);
            resolve(JSON.parse(decryptedValue));
          } catch (decryptError) {
            try {
              // Fallback: try to parse as unencrypted JSON
              resolve(JSON.parse(storedValue));
            } catch (parseError) {
              // If both fail, return the raw value
              resolve(storedValue);
            }
          }
        } else {
          resolve(null);
        }
      };
    });
  }

  async removeItem(key: string): Promise<void> {
    if (!this.db) await this.initDB();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['userData'], 'readwrite');
      const store = transaction.objectStore('userData');
      const request = store.delete(key);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }
}